import {StackScreenProps} from '@react-navigation/stack';
import produce from 'immer';
import {Box, Button, FormControl} from 'native-base';
import React, {useEffect, useRef} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {TextInput} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {
  Transaction,
  TransactionsDocument,
  TransactionsQuery,
  TransactionsQueryVariables,
  useSendTransactionMutation
} from '../../api/generated';
import {Input} from '../../components/Input';
import {KeyboardAvoidingView} from '../../components/KeyboardAvoidingView';
import {PropertyList} from '../../components/PropertyList';
import {formatIntAmount, formatPhone} from '../../lib/formatters';
import {triggerHapticFeedback} from '../../lib/hapticFeedback';
import i18n from '../../lib/i18n';
import {Nav, NavParams} from '../navigation';
import {config} from '../../stage.config';

const MAX_AMOUNT = config.production ? 1000 : 100000;

export const CreatePaymentMessageScreen: React.FC<
  StackScreenProps<NavParams, Nav.CreatePaymentMessageScreen>
> = ({navigation, route}) => {
  const {amount, phoneNumber, message: defaultMessage = ''} = route.params;
  const inputRef = useRef<TextInput>(null);
  const {
    control,
    handleSubmit,
    formState: {errors}
  } = useForm({defaultValues: {message: defaultMessage}});
  const [sendTransaction, {loading}] = useSendTransactionMutation();
  const isSending = useRef(false);
  // Amount and message can come from a deep link or a scanned QR
  const isInvalid = !Number.isInteger(amount) || amount <= 0 || amount > MAX_AMOUNT;

  useEffect(() => {
    return navigation.addListener('transitionEnd', (nativeEvent) => {
      if (!nativeEvent.data.closing) {
        inputRef.current?.focus();
      }
    });
  }, [navigation]);

  const onSubmit = async ({message}: {message: string}) => {
    if (isInvalid) return;
    // Keyboard "done" and Send can both fire before `loading` re-renders
    if (isSending.current) return;
    isSending.current = true;
    try {
      const {data} = await sendTransaction({
        variables: {
          input: {
            amount,
            message,
            phoneNumber
          }
        },
        update(cache, {data}) {
          if (!data?.sendTransaction) return;
          const trx: Transaction = data.sendTransaction;
          const result = cache.readQuery<TransactionsQuery, TransactionsQueryVariables>({
            query: TransactionsDocument
          });
          if (!result?.transactions) return;
          cache.writeQuery<TransactionsQuery, TransactionsQueryVariables>({
            query: TransactionsDocument,
            variables: {from: 0, size: 20},
            data: produce(result, (draft) => {
              draft.transactions.items.pop();
              draft.transactions.items.unshift(trx);
            })
          });
        }
      });
      const trx = data?.sendTransaction;
      if (trx) {
        triggerHapticFeedback();
        navigation.navigate(Nav.HomeScreen);
      }
    } finally {
      isSending.current = false;
    }
  };

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView offset={125}>
        <PropertyList
          properties={[
            {label: i18n.t('amount'), value: formatIntAmount(amount)},
            {
              label: i18n.t('toPhoneNumber'),
              value: formatPhone(phoneNumber)
            }
          ]}
        />
        <Box style={[t.flex1, t.m4]}>
          <FormControl isInvalid={'message' in errors}>
            <Controller
              name="message"
              control={control}
              render={({field: {onChange, onBlur, value}}) => (
                <Input
                  ref={inputRef}
                  value={value}
                  placeholder={i18n.t('messagePlaceholder')}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  autoCapitalize="none"
                  onSubmitEditing={handleSubmit(onSubmit)}
                  returnKeyType="done"
                />
              )}
            />
          </FormControl>
        </Box>
        <Box style={[t.p4]}>
          <Button onPress={handleSubmit(onSubmit)} isLoading={loading} isDisabled={isInvalid}>
            {i18n.t('send')}
          </Button>
        </Box>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
